import {
  format,
  isToday,
  isValid,
  isYesterday,
  parse,
  parseISO,
  differenceInCalendarDays,
  startOfMonth,
  endOfMonth,
  differenceInDays,
} from "date-fns";

export const ROUTE = {
  PROJECTS: () => `/work/projects`,
  PROJECT_NEW: () => `${ROUTE.PROJECTS()}/+`,
  PROJECT: (item) => `${ROUTE.PROJECTS()}/${item}`,
  PROJECT_EDIT: (item) => `${ROUTE.PROJECT(item)}/edit`,
  PROJECT_BILLABLES: (item) => `${ROUTE.PROJECT(item)}/billables`,
  PROJECT_JOBS: (item) => `${ROUTE.PROJECT(item)}/jobs`,
  PROJECT_MILESTONES: (item) => `${ROUTE.PROJECT(item)}/milestones`,
  PROJECT_BOOKMARKS: (item) => `${ROUTE.PROJECT(item)}/bookmarks`,
  PROJECT_DELIVERABLES: (item) => `${ROUTE.PROJECT(item)}/deliverables`,
  PROJECT_INTEGRATIONS: (item) => `${ROUTE.PROJECT(item)}/integrations`,
  PROJECT_CONTRACTS: (item) => `${ROUTE.PROJECT(item)}/contracts`,
  PROJECT_REVENUE: (item) => `${ROUTE.PROJECT(item)}/revenue`,
  PROJECT_SERVICES: (item) => `${ROUTE.PROJECT(item)}/services`,
  PROJECT_ANALYTICS: (item) => `${ROUTE.PROJECT(item)}/analytics`,
  PROJECT_SURVEYS: (item) => `${ROUTE.PROJECT(item)}/surveys`,
  PROJECT_INTEGRATIONS_CHAT_SERVICE: (project, projectServiceId, chatId) =>
    `${ROUTE.PROJECT_INTEGRATIONS(
      project,
    )}/chat-services/${projectServiceId}/${chatId}`,
  PROJECT_INTEGRATIONS_DRIVE_SERVICE: (project, projectServiceId, driveId) =>
    `${ROUTE.PROJECT_INTEGRATIONS(
      project,
    )}/drive-services/${projectServiceId}/${driveId}`,
  PROJECT_INTEGRATIONS_REPOSITORY_SERVICE: (
    project,
    projectServiceId,
    repoId,
  ) =>
    `${ROUTE.PROJECT_INTEGRATIONS(
      project,
    )}/repository-services/${projectServiceId}/${repoId}`,

  JOBS: () => `/work/jobs`,
  JOB: (item) => `${ROUTE.JOBS()}/${item}`,
  JOB_TIMESHEET_HISTORY: (item) => `${ROUTE.JOB(item)}/timesheet/history`,
  JOB_TIMESHEET_HISTORY_DATE: (item) =>
    `${ROUTE.JOB(item)}/timesheet/history/date`,
  JOB_EDIT: (item) => `${ROUTE.JOB(item)}/edit`,
  JOB_EXTENSIONS: (item) => `${ROUTE.JOB(item)}/extensions`,
  JOB_EXTENSION: (item) => `${ROUTE.JOB(item.job.id)}/extensions/${item.id}`,
  JOB_EMPLOYEES: (item) => `${ROUTE.JOB(item)}/employees`,
  JOB_EMPLOYEE: (job, item) => `${ROUTE.JOB_EMPLOYEES(job)}/${item}/edit`,

  CONTRACTS: () => `/work/contracts`,
  CONTRACT: (item) => `${ROUTE.CONTRACTS()}/${item}`,

  BILLABLES: () => `/work/billables`,
  BILLABLE: (item) => `${ROUTE.BILLABLES()}/${item}`,
  BILLABLE_EDIT: (item) => `${ROUTE.BILLABLE(item)}/edit`,
  BILLABLE_EXTENSIONS: (item) => `${ROUTE.BILLABLE(item)}/extensions`,
  BILLABLE_EXTENSION: (billable, extension) =>
    `${ROUTE.BILLABLE(billable)}/extensions/${extension}`,
  BILLABLE_ACTIVITY: (item) => `${ROUTE.BILLABLE(item)}/activity`,


  MILESTONES: () => `/work/milestones`,
  MILESTONE: (milestoneId) => `${ROUTE.MILESTONES()}/${milestoneId}`,
  MILESTONE_EDIT: (milestoneId) => `${ROUTE.MILESTONE(milestoneId)}/edit`,

  DEPARTMENTS: () => `/master-data/departments`,
  DEPARTMENT: (item) => `${ROUTE.DEPARTMENTS()}/${item}`,

  EMPLOYEE: (item) => `/organization/employees/${item}`,

  TIMESHEET_ENTRY: (item) => ``,
};

export const TIMESHEET_LOG_DAY_VIEW = "timesheet/logs";
export const TIMESHEET_DRAFT_DAY_VIEW = "timesheet/draft";

export const TIMESHEET_MYPROJECTS_REVIEW = "timesheet/reviews";
export const TIMESHEET_MYPROJECTS_DAY = "timesheet/reviews";

export const TODAY = formatDateForAPI(new Date());

export const CURRENT_MONTH_START_DATE = formatDateForAPI(
  startOfMonth(new Date()),
);

export const CURRENT_MONTH_END_DATE = formatDateForAPI(endOfMonth(new Date()));

const knownFormats = ["yyyy-MM-dd", "dd-MM-yyyy"];

// External deep-link helpers
export const DISCORD_GUILD_ID = "715898326116991027";
export const LINKS = {
  DISCORD_CHANNEL: (guildId, channelId) =>
    `discord://https://discord.com/channels/${guildId}/${channelId}`,
  DRIVE_FOLDER: (folderId) =>
    `drive://https://drive.google.com/drive/folders/${folderId}`,
  DRIVE_FOLDER_WEB: (folderId) =>
    `https://drive.google.com/drive/folders/${folderId}`,
};

export function parseFlexibleDate(dateStr) {
  // Handle null, undefined, or empty values
  if (!dateStr) {
    return null;
  }
  if (dateStr instanceof Date) {
    return isValid(dateStr) ? dateStr : null;
  }
  for (const formatString of knownFormats) {
    const parsed = parse(dateStr, formatString, new Date());
    if (isValid(parsed)) {
      return parsed;
    }
  }

  return null; // or throw an error if preferred
}

export function filterNavItemsByPermission(navItems, permissions) {
  if (!navItems || !permissions) return [];

  return navItems
    .map((item) => {
      // Check if this item has a permission requirement
      if (item.requiredPermission && !permissions[item.requiredPermission]) {
        return null; // Filter out items user doesn't have permission for
      }

      // Also check if submenu items have permissions
      const filteredItems = item.items
        ? item.items.filter(
          (subItem) =>
            !subItem.requiredPermission ||
            permissions[subItem.requiredPermission],
        )
        : [];

      // Return item with filtered subitems
      return {
        ...item,
        items: filteredItems.length > 0 ? filteredItems : undefined,
      };
    })
    .filter(Boolean); // Remove null items
}

/**
 * Calculates total hours from entries
 * @param {Array} entries - Array of timesheet entries
 * @param {string} field - Field to sum (logged_hours or approved_hours)
 * @returns {number} Total hours
 */
export const calculateTotalHours = (entries, field) => {
  return entries.reduce((sum, entry) => sum + (Number(entry[field]) || 0), 0);
};

export const calculateTotalHoursWithConsumptionFactor = (entries, field) => {
  return entries.reduce(
    (sum, entry) =>
      sum +
      (Number(entry[field]) || 0) *
      ((entry.consumption_factor || entry.employee.consumption_factor) / 100),
    0,
  );
};

export function formatDateForAPI(dateString) {
  const date = parseFlexibleDate(dateString);

  return date ? format(date, "yyyy-MM-dd") : "";
}

export function formatDateToDMY(dateString) {
  const date = parseFlexibleDate(dateString);

  const formattedDate = format(date, "dd-MM-yyyy");

  return date ? formattedDate : "";
}

/**
 * Formats a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(dateString) {
  // const date = parseFlexibleDate(dateString);
  if (!dateString) return null;
  const formattedDate = format(dateString, "d MMM, y");

  return formattedDate;
}

export function formatDateForDistance(dateA, dateB) {
  const parsedA =
    parseFlexibleDate(dateA) ||
    (typeof dateA === "string" ? parseISO(dateA) : dateA);
  const parsedB =
    parseFlexibleDate(dateB) ||
    (typeof dateB === "string" ? parseISO(dateB) : dateB);
  if (!parsedA || !parsedB || !isValid(parsedA) || !isValid(parsedB))
    return null;
  return Math.abs(differenceInCalendarDays(parsedA, parsedB));
}

export function formatDateForLogOptions(dateString) {
  const date = parseFlexibleDate(dateString);

  const formattedDate = format(date, "EEEE d MMM, y");

  return formattedDate;
}

/**
 * Gets the last N days including today
 * @param {number} numberOfDays - Number of days to get
 * @returns {string[]} Array of dates in ISO format
 */
export const getLastNDays = (numberOfDays) => {
  const today = new Date();
  return Array.from({ length: numberOfDays }, (_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    return date.toISOString().split("T")[0];
  });
};

export function getDayFromDate(dateString) {
  const date =
    typeof dateString === "string" ? parseISO(dateString) : dateString;

  const day = format(date, "EEEE");
  if (isToday(date)) {
    return day + " (Today)";
  } else if (isYesterday(date)) {
    return day + " (Yesterday)";
  } else {
    return day;
  }
}

export const generateYAxisTicks = (max) => {
  const interval = 5000000;
  const ticks = [];
  for (let i = 0; i <= max; i += interval) {
    ticks.push(i);
  }
  if (ticks[ticks.length - 1] < max) {
    ticks.push(Math.ceil(max / interval) * interval);
  }
  return ticks;
};

export const getBillableExtensionDays = (billable) => {
  const billingDate = billable.billing_date;
  const extensions = billable.extensions;
  let extensionDays = null;
  if (Array.isArray(extensions) && extensions.length > 0 && billingDate) {
    const firstExtension = extensions?.[0]?.start_date;
    if (firstExtension) {
      extensionDays = Math.abs(
        differenceInDays(parseISO(firstExtension), parseISO(billingDate)),
      );
    }
  }
  return extensionDays;
};

export const getJobExtensionDays = (job) => {
  const endDate = job.end_date;
  const extensions = job.extensions;
  let extensionDays = null;
  if (Array.isArray(extensions) && extensions.length > 0 && endDate) {
    const firstExtension = extensions?.[0]?.start_date;
    if (firstExtension) {
      extensionDays = Math.abs(
        differenceInDays(parseISO(firstExtension), parseISO(endDate)),
      );
    }
  }
  return extensionDays;
};
